import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu, Cross, Book, Globe, Shield } from 'lucide-react';

const NotreDame: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "University of Notre Dame";

    return (
        <div className="min-h-screen bg-[#0C2340] font-serif text-white selection:bg-[#C99700] selection:text-[#0C2340]">
             {/* Header */}
            <nav className="bg-[#0C2340] py-6 px-6 md:px-12 sticky top-0 z-50 border-b border-[#C99700]">
                <div className="max-w-[1400px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-2 text-[#C99700]">
                        <span className="text-3xl font-normal tracking-wide">{schoolName.toUpperCase()}</span>
                    </div>

                    <div className="hidden lg:flex gap-8 font-sans font-bold text-white text-sm uppercase tracking-widest">
                        <a href="#" className="hover:text-[#C99700] transition-colors">Study</a>
                        <a href="#" className="hover:text-[#C99700] transition-colors">Research</a>
                        <a href="#" className="hover:text-[#C99700] transition-colors">Faith</a>
                        <a href="#" className="hover:text-[#C99700] transition-colors">Global</a>
                        <a href="#" className="hover:text-[#C99700] transition-colors">Athletics</a>
                    </div>

                    <div className="flex gap-4">
                        <Search className="text-[#C99700] cursor-pointer" size={24} />
                        <Menu className="text-[#C99700] cursor-pointer lg:hidden" size={24} />
                    </div>
                </div>
            </nav>

            {/* Hero - The Golden Dome Vibe */}
            <header className="relative h-[700px]">
                 <img 
                    src="https://images.unsplash.com/photo-1574688691523-28945625bf92?q=80&w=2008&auto=format&fit=crop" 
                    alt="Golden Dome" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0C2340] via-[#0C2340]/60 to-transparent"></div>
                
                <div className="absolute top-1/2 -translate-y-1/2 left-0 w-full px-8 md:px-12">
                     <div className="max-w-2xl text-white">
                         <h1 className="text-5xl md:text-7xl mb-8 leading-tight">
                             God, Country, Notre Dame.
                         </h1>
                         <p className="text-xl md:text-2xl mb-12 font-sans font-light text-gray-200">
                             A preeminent research university that offers a world-class education inspired by a Catholic mission.
                         </p>
                         <div className="flex gap-4">
                             <Button className="bg-[#C99700] hover:bg-[#a67c00] text-[#0C2340] font-sans font-bold uppercase tracking-widest px-8 h-14 rounded-sm">
                                 Apply
                             </Button>
                             <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#0C2340] font-sans font-bold uppercase tracking-widest px-8 h-14 rounded-sm bg-transparent">
                                 Visit
                             </Button>
                         </div>
                     </div>
                </div>
            </header>

            {/* Pillars */}
            <section className="bg-white text-[#0C2340] py-24 px-6 md:px-12">
                <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
                    <div className="flex flex-col items-center group cursor-pointer">
                        <Book size={48} className="text-[#C99700] mb-6 group-hover:scale-110 transition-transform" />
                        <h3 className="text-2xl font-bold mb-4">Intellectual Excellence</h3>
                        <p className="font-sans text-gray-600 leading-relaxed">Rigorous academics that challenge the mind and cultivate wisdom.</p>
                    </div>
                    <div className="flex flex-col items-center group cursor-pointer">
                        <Cross size={48} className="text-[#C99700] mb-6 group-hover:scale-110 transition-transform" />
                        <h3 className="text-2xl font-bold mb-4">Catholic Character</h3>
                        <p className="font-sans text-gray-600 leading-relaxed">Developing the heart and spirit in a community of faith.</p>
                    </div>
                    <div className="flex flex-col items-center group cursor-pointer">
                        <Globe size={48} className="text-[#C99700] mb-6 group-hover:scale-110 transition-transform" />
                        <h3 className="text-2xl font-bold mb-4">Global Impact</h3>
                        <p className="font-sans text-gray-600 leading-relaxed">Using knowledge to serve the common good around the world.</p>
                    </div>
                </div>
            </section>

             {/* News - Grid Layout */}
             <section className="bg-[#f2f2f2] py-24 px-6 md:px-12 text-[#0C2340]">
                  <div className="max-w-[1400px] mx-auto">
                      <h2 className="text-3xl font-bold mb-12">Stories from {schoolName}</h2>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                           <div className="col-span-1 md:col-span-2 relative group cursor-pointer h-[400px] md:h-auto">
                               <img src="https://images.unsplash.com/photo-1544928147-79a2af1f9850?q=80&w=2066&auto=format&fit=crop" className="w-full h-full object-cover rounded-sm" alt="Feature" />
                               <div className="absolute inset-0 bg-gradient-to-t from-[#0C2340] to-transparent opacity-90 transition-opacity group-hover:opacity-100 flex flex-col justify-end p-8">
                                   <span className="text-[#C99700] font-sans font-bold text-xs uppercase tracking-widest mb-2">Feature</span>
                                   <h3 className="text-white text-2xl font-bold">University announces new poverty institute</h3>
                               </div>
                           </div>
                           
                           <div className="bg-white p-8 rounded-sm shadow-sm hover:shadow-md transition-all cursor-pointer group">
                                <span className="text-gray-400 font-sans font-bold text-xs uppercase tracking-widest mb-4 block">Student Life</span>
                                <h4 className="text-lg font-bold mb-3 group-hover:text-[#C99700] transition-colors">Irish Guard brings tradition to stadium</h4>
                           </div>
                            <div className="bg-white p-8 rounded-sm shadow-sm hover:shadow-md transition-all cursor-pointer group">
                                <span className="text-gray-400 font-sans font-bold text-xs uppercase tracking-widest mb-4 block">Reasearch</span>
                                <h4 className="text-lg font-bold mb-3 group-hover:text-[#C99700] transition-colors">Study links sleep to memory retention</h4>
                           </div>
                            <div className="bg-white p-8 rounded-sm shadow-sm hover:shadow-md transition-all cursor-pointer group">
                                <span className="text-gray-400 font-sans font-bold text-xs uppercase tracking-widest mb-4 block">Arts</span>
                                <h4 className="text-lg font-bold mb-3 group-hover:text-[#C99700] transition-colors">Shakespeare festival returns to campus</h4>
                           </div>
                            <div className="bg-white p-8 rounded-sm shadow-sm hover:shadow-md transition-all cursor-pointer group">
                                <span className="text-gray-400 font-sans font-bold text-xs uppercase tracking-widest mb-4 block">Alumni</span>
                                <h4 className="text-lg font-bold mb-3 group-hover:text-[#C99700] transition-colors">Award winning architect credits {schoolName.split(' ').pop() === 'Dame' ? 'ND' : 'university'} education</h4>
                           </div>
                      </div>
                  </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#0C2340] text-white py-16 px-6 md:px-12 border-t border-[#C99700]/30">
                 <div className="max-w-[1400px] mx-auto text-center md:text-left grid grid-cols-1 md:grid-cols-4 gap-12 font-sans">
                      <div className="col-span-1 md:col-span-2">
                           <span className="text-2xl font-serif mb-6 block text-[#C99700]">{schoolName}</span>
                           <p className="text-sm text-gray-400">
                               Notre Dame, IN 46556 USA <br/>
                               574-631-5000
                           </p>
                      </div>

                      <div>
                          <h4 className="font-bold text-[#C99700] text-xs uppercase tracking-widest mb-6">Resources</h4>
                          <ul className="space-y-3 text-sm text-gray-300">
                              <li><a href="#" className="hover:text-white">Search</a></li>
                              <li><a href="#" className="hover:text-white">Directory</a></li>
                              <li><a href="#" className="hover:text-white">Employment</a></li>
                              <li><a href="#" className="hover:text-white">Accessibility</a></li>
                          </ul>
                      </div>

                      <div>
                          <h4 className="font-bold text-[#C99700] text-xs uppercase tracking-widest mb-6">Connect</h4>
                           <ul className="space-y-3 text-sm text-gray-300">
                              <li><a href="#" className="hover:text-white">Facebook</a></li>
                              <li><a href="#" className="hover:text-white">Twitter</a></li>
                              <li><a href="#" className="hover:text-white">Instagram</a></li>
                              <li><a href="#" className="hover:text-white">YouTube</a></li>
                          </ul>
                      </div>
                 </div>
                 <div className="max-w-[1400px] mx-auto mt-12 pt-8 border-t border-[#C99700]/10 text-center text-xs text-gray-500">
                     © 2026 {schoolName}
                 </div>
             </footer>
        </div>
    );
};

export default NotreDame;
